
import React, { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {ChevronRight, ChevronDown, ShoppingBag, Store, Beaker, Briefcase, Coffee, Package, Gift, Settings} from 'lucide-react'

interface ProductBreadcrumbProps {
  category?: string
  productName?: string
}

const ProductBreadcrumb: React.FC<ProductBreadcrumbProps> = ({ category, productName }) => {
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false)
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)
  const storeDropdownRef = useRef<HTMLDivElement>(null)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  
  const currentLang = location.pathname.startsWith('/en') ? 'en' : 'zh'

  // 關閉下拉選單當點擊外部時
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (storeDropdownRef.current && !storeDropdownRef.current.contains(event.target as Node)) {
        setIsStoreDropdownOpen(false)
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // 商店區域選項
  const storeOptions = {
    zh: [
      {
        name: '線上購物',
        href: '/zh/shop',
        icon: ShoppingBag,
        description: '精選咖啡豆與器具',
        color: 'text-blue-600 dark:text-blue-400'
      },
      {
        name: '實體店面',
        href: '/zh/store',
        icon: Store,
        description: '信義店・大安店',
        color: 'text-green-600 dark:text-green-400'
      },
      {
        name: '醞玖製研中心',
        href: '/zh/lab',
        icon: Beaker,
        description: '沖煮指南・豆子故事',
        color: 'text-purple-600 dark:text-purple-400'
      },
      {
        name: 'B2B批發',
        href: '/zh/wholesale',
        icon: Briefcase,
        description: '合作夥伴・烘豆代工',
        color: 'text-amber-600 dark:text-amber-400'
      }
    ],
    en: [
      {
        name: 'Online Shop',
        href: '/en/shop',
        icon: ShoppingBag,
        description: 'Premium Coffee & Equipment',
        color: 'text-blue-600 dark:text-blue-400'
      },
      {
        name: 'Physical Stores',
        href: '/en/store',
        icon: Store,
        description: 'Xinyi・Daan Locations',
        color: 'text-green-600 dark:text-green-400'
      },
      {
        name: 'Research Lab',
        href: '/en/lab',
        icon: Beaker,
        description: 'Guides・Bean Stories',
        color: 'text-purple-600 dark:text-purple-400'
      },
      {
        name: 'B2B Wholesale',
        href: '/en/wholesale',
        icon: Briefcase,
        description: 'Partners・Roasting Services',
        color: 'text-amber-600 dark:text-amber-400'
      }
    ]
  }

  // 商品分類選項
  const categoryOptions = {
    zh: [
      {
        id: 'beans',
        name: '咖啡豆',
        href: `/zh/shop/beans`,
        icon: Coffee,
        description: '精選單品・綜合豆',
        color: 'text-amber-700 dark:text-amber-400'
      },
      {
        id: 'dripbag',
        name: '濾掛咖啡',
        href: `/zh/shop/dripbag`,
        icon: Package,
        description: '便利沖煮・隨時享用',
        color: 'text-blue-700 dark:text-blue-400'
      },
      {
        id: 'gift',
        name: '禮盒系列',
        href: `/zh/shop/gift`,
        icon: Gift,
        description: '精美包裝・送禮首選',
        color: 'text-red-700 dark:text-red-400'
      },
      {
        id: 'gear',
        name: '器具設備',
        href: `/zh/shop/gear`,
        icon: Settings,
        description: '沖煮器具・專業設備',
        color: 'text-gray-700 dark:text-gray-400'
      }
    ],
    en: [
      {
        id: 'beans',
        name: 'Coffee Beans',
        href: `/en/shop/beans`,
        icon: Coffee,
        description: 'Single Origin・Blends',
        color: 'text-amber-700 dark:text-amber-400'
      },
      {
        id: 'dripbag',
        name: 'Drip Bags',
        href: `/en/shop/dripbag`,
        icon: Package,
        description: 'Convenient・Ready to Brew',
        color: 'text-blue-700 dark:text-blue-400'
      },
      {
        id: 'gift',
        name: 'Gift Sets',
        href: `/en/shop/gift`,
        icon: Gift,
        description: 'Premium Packaging・Perfect Gifts',
        color: 'text-red-700 dark:text-red-400'
      },
      {
        id: 'gear',
        name: 'Equipment',
        href: `/en/shop/gear`,
        icon: Settings,
        description: 'Brewing Tools・Professional Gear',
        color: 'text-gray-700 dark:text-gray-400'
      }
    ]
  }

  // 分類名稱映射（手機版簡化）
  const categoryNames = {
    zh: {
      beans: '咖啡豆',
      dripbag: '濾掛',
      gift: '禮盒',
      gear: '器具'
    },
    en: {
      beans: 'Beans',
      dripbag: 'Drip Bags',
      gift: 'Gifts',
      gear: 'Gear'
    }
  }

  // 商店名稱簡化（手機版）
  const storeNames = {
    zh: '購物',
    en: 'Shop'
  }

  const getCurrentCategoryName = () => {
    if (!category) return currentLang === 'zh' ? '商品' : 'Products'
    return categoryNames[currentLang][category as keyof typeof categoryNames.zh] || category
  }

  return (
    <div className="fixed top-20 lg:top-[64px] left-0 right-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 z-40 backdrop-blur-md bg-opacity-95 dark:bg-opacity-95 transition-colors duration-300">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-[50px]">
        <div className="flex items-center justify-between h-12">
          {/* 麵包屑導航 */}
          <nav className="flex items-center space-x-2 text-sm min-w-0 flex-1">
            {/* 首頁 */}
            <Link
              to={`/${currentLang}`}
              className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 flex-shrink-0 font-light tracking-wide"
            >
              {currentLang === 'zh' ? '首頁' : 'Home'}
            </Link>

            <ChevronRight size={12} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />

            {/* 商店區域 - 下拉選單 */}
            <div className="relative flex-shrink-0" ref={storeDropdownRef}>
              <button
                onClick={() => setIsStoreDropdownOpen(!isStoreDropdownOpen)}
                className="flex items-center space-x-1 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 font-light tracking-wide px-2 h-8"
              >
                <span>
                  {storeNames[currentLang]}
                </span>
                <ChevronDown 
                  size={12} 
                  className={`text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isStoreDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* 商店區域下拉選單 */}
              <AnimatePresence>
                {isStoreDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute top-full left-0 mt-2 w-72 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl z-[60] overflow-hidden"
                  >
                    <div className="p-2">
                      {storeOptions[currentLang].map((option, index) => {
                        const Icon = option.icon
                        return (
                          <Link
                            key={option.name}
                            to={option.href}
                            onClick={() => setIsStoreDropdownOpen(false)}
                            className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 group"
                          >
                            <div className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-600 group-hover:bg-white dark:group-hover:bg-gray-500 transition-colors duration-200`}>
                              <Icon size={18} className={option.color} />
                            </div>
                            <div className="flex-1">
                              <div className="text-gray-900 dark:text-gray-100 font-medium group-hover:text-gray-800 dark:group-hover:text-gray-50 font-primary">
                                {option.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-primary">
                                {option.description}
                              </div>
                            </div>
                          </Link>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <ChevronRight size={12} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />

            {/* 分類 - 下拉選單 */}
            <div className="relative flex-shrink-0" ref={categoryDropdownRef}>
              <button
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="flex items-center space-x-1 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors duration-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 font-light tracking-wide px-2 h-8"
              >
                <span>
                  {getCurrentCategoryName()}
                </span>
                <ChevronDown 
                  size={12} 
                  className={`text-gray-400 dark:text-gray-500 transition-transform duration-200 ${isCategoryDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* 分類下拉選單 */}
              <AnimatePresence>
                {isCategoryDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute top-full left-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl z-[60] overflow-hidden"
                  >
                    <div className="p-2">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider font-primary">
                        {currentLang === 'zh' ? '商品分類' : 'Product Categories'}
                      </div>
                      {categoryOptions[currentLang].map((option, index) => {
                        const Icon = option.icon
                        const isActive = category === option.id
                        return (
                          <Link
                            key={option.id}
                            to={option.href}
                            onClick={() => setIsCategoryDropdownOpen(false)}
                            className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 group ${
                              isActive 
                                ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700'
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <div className={`p-2 rounded-lg transition-colors duration-200 ${
                              isActive
                                ? 'bg-blue-100 dark:bg-blue-800'
                                : 'bg-gray-100 dark:bg-gray-600 group-hover:bg-white dark:group-hover:bg-gray-500'
                            }`}>
                              <Icon size={18} className={isActive ? 'text-blue-600 dark:text-blue-400' : option.color} />
                            </div>
                            <div className="flex-1">
                              <div className={`font-medium transition-colors duration-200 font-primary ${
                                isActive
                                  ? 'text-blue-800 dark:text-blue-200'
                                  : 'text-gray-900 dark:text-gray-100 group-hover:text-gray-800 dark:group-hover:text-gray-50'
                              }`}>
                                {option.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-primary">
                                {option.description}
                              </div>
                            </div>
                            {isActive && (
                              <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400"></div>
                            )}
                          </Link>
                        )
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* 產品名稱（如果有的話）- 手機版截短 */}
            {productName && (
              <>
                <ChevronRight size={12} className="text-gray-400 dark:text-gray-500 flex-shrink-0" />
                <span className="text-gray-900 dark:text-gray-100 truncate min-w-0 max-w-[120px] sm:max-w-xs font-light tracking-wide">
                  {productName}
                </span>
              </>
            )}
          </nav>

          {/* 右側額外資訊（桌面版） */}
          <div className="hidden lg:flex items-center text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
            <span className="font-light tracking-wide">{currentLang === 'zh' ? '商品詳情' : 'Product Details'}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProductBreadcrumb
